import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn } from '../components/Btn.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { Field, PasswordInput } from '../components/Field.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { useBranding } from '../lib/branding.tsx';
import { PoweredByFooter } from '../components/PoweredByFooter.tsx';
import { setAdminAccessToken } from '../lib/auth/adminToken.ts';
import { getAdminSupabase } from '../lib/supabase.ts';

const MIN_PASSWORD_LENGTH = 8;

export function ResetPassword() {
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'redeeming' | 'set-password'>('redeeming');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const ranRef = useRef(false);
  const brandName = branding.companyName?.replace(/ Safety.*$/i, '') ?? 'Locate2u';

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    let supabase;
    try {
      supabase = getAdminSupabase();
    } catch (err) {
      setError(err);
      return;
    }

    const hash = window.location.hash.replace(/^#/, '');
    const fragment = hash ? new URLSearchParams(hash) : null;
    const query = new URLSearchParams(window.location.search);

    const access = fragment?.get('access_token');
    const refresh = fragment?.get('refresh_token');
    const code = query.get('code');
    const tokenHash = query.get('token_hash');

    const redeem = async (): Promise<string | null | undefined> => {
      if (access && refresh) {
        const { data, error: sessionErr } = await supabase.auth.setSession({
          access_token: access,
          refresh_token: refresh,
        });
        if (sessionErr) throw sessionErr;
        setAdminAccessToken(access);
        return data.user?.email ?? null;
      }
      if (code) {
        const { data, error: exchangeErr } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeErr) throw exchangeErr;
        setAdminAccessToken(data.session?.access_token ?? null);
        return data.user?.email ?? null;
      }
      if (tokenHash) {
        const { data, error: otpErr } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'recovery',
        });
        if (otpErr) throw otpErr;
        setAdminAccessToken(data.session?.access_token ?? null);
        return data.user?.email ?? null;
      }
      throw new Error('Reset link is missing the credentials needed to continue.');
    };

    void redeem()
      .then((resolvedEmail) => {
        window.history.replaceState({}, '', '/admin/reset-password');
        setEmail(resolvedEmail ?? null);
        setPhase('set-password');
      })
      .catch((err) => setError(err));
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(new Error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`));
      return;
    }
    if (password !== confirm) {
      setError(new Error('Passwords do not match.'));
      return;
    }
    setSubmitting(true);
    try {
      const supabase = getAdminSupabase();
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) throw updateErr;
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === 'redeeming') {
    if (error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <h1 className="text-lg font-semibold text-slate-900">
              {branding.companyName ?? 'Compliance'}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              We couldn’t process your reset link. It may have expired or already been used.
            </p>
            <div className="mt-4">
              <ErrorMessage error={error} />
            </div>
            <div className="mt-4">
              <a href="/admin/forgot-password" className="text-sm text-blue-600 hover:underline">
                Request a new reset link
              </a>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50">
        <CenteredSpinner />
        <p className="text-sm text-slate-500">Verifying your reset link…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 text-center">
          {branding.logoUrl && (
            <img src={branding.logoUrl} alt={brandName} className="mx-auto mb-3 h-10 w-auto" />
          )}
          <div
            className="brand-wordmark mb-1 text-2xl"
            style={{ color: 'var(--brand-primary)' }}
            aria-label="Safety & Compliance"
          >
            Safety<span className="accent">&amp;</span>Compliance
          </div>
          <PoweredByFooter brandName={brandName} />
          <p className="mt-4 text-sm font-medium text-slate-700">Reset your password</p>
          <p className="mt-1 text-sm text-slate-500">
            Enter a new password to finish resetting your account.
          </p>
          {email && <p className="mt-1 text-sm font-medium text-slate-700">{email}</p>}
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          <Field label="New password" required hint={`At least ${MIN_PASSWORD_LENGTH} characters`}>
            <PasswordInput
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
            />
          </Field>
          <Field label="Confirm password" required>
            <PasswordInput
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
            />
          </Field>
          <ErrorMessage error={error} />
          <Btn
            type="submit"
            loading={submitting}
            disabled={!password || !confirm}
            className="w-full"
          >
            Update password and sign in
          </Btn>
        </form>
      </div>
    </div>
  );
}
